import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";

import router from "core/router";
import useFormRequest from "core/hookForm/hooks/useFormRequest";
import { CampaignEmailSourceEnum, CampaignsDataStatusEnum, ElectronicCommunicationCampaignDto, FormState } from "types";
import { Pages, Tabs } from "routeConstants";
import { campaignApi } from "serverApi/index";

import getValidationSchema from "../validationSchema";
import { CampaignElCommunicationFormValues } from "../types/CampaignElCommunicationFormValues";

const initialCampaignData = {
    contractNumber: "...",
    email: "...",
    productCrm: "...",
};

export function useElCommunicationCampaignForm() {
    const { guid, idObject } = useParams();
    const dispatch = useDispatch();

    const [isProcessing, setIsProcessing] = useState(true);
    const [formState, setFormState] = useState<FormState>(FormState.EDIT);
    const [campaignData, setCampaignData] = useState<ElectronicCommunicationCampaignDto>(initialCampaignData);

    // Memoized value based on static location
    const emailDataSource = useMemo(() => {
        const { pathname, search } = window.location;
        const pageInformation = router.parseLocation(pathname, search);
        return pageInformation?.innerName === Tabs.ELECTRONIC_COMMUNICATION_C360_EMAIL
            ? CampaignEmailSourceEnum.CLIENT_360
            : CampaignEmailSourceEnum.CONTRACT;
    }, []);

    const initialValues: CampaignElCommunicationFormValues = {
        guid,
        idObject: Number(idObject),
        campaignEmailSource: emailDataSource,
        agreement: false,
    };

    const form = useForm<CampaignElCommunicationFormValues>({
        defaultValues: initialValues,
        resolver: yupResolver(getValidationSchema()),
    });

    const handleRequest = useFormRequest(form);

    // Fetch campaign data
    const fetchCampaignData = useCallback(async () => {
        setIsProcessing(true);
        try {
            const apiMethod =
                emailDataSource === CampaignEmailSourceEnum.CLIENT_360
                    ? campaignApi.getElectronicCommunicationEmailFromClient360Data
                    : campaignApi.getElectronicCommunicationData;

            const [error, res] = await apiMethod(guid, Number(idObject));

            if (error || res?.body?.result !== CampaignsDataStatusEnum.OK) {
                setFormState(FormState.ERROR);
            } else {
                setCampaignData(res.body?.data);
            }
        } catch {
            setFormState(FormState.ERROR);
        } finally {
            setIsProcessing(false);
        }
    }, [emailDataSource, guid, idObject]);

    useEffect(() => {
        fetchCampaignData();
    }, [fetchCampaignData]);

    const onSubmit = useCallback(
        async (data: CampaignElCommunicationFormValues) => {
            setIsProcessing(true);

            const handleError = () => {
                setFormState(FormState.ERROR);
                setIsProcessing(false);
            };

            const handleSuccess = (response) => {
                if (response.body?.result === CampaignsDataStatusEnum.UPDATED) {
                    dispatch(router.navigate(Pages.CAMPAIGN, Tabs.ELECTRONIC_COMMUNICATION_SUCCESS));
                } else {
                    handleError();
                }
            };

            await handleRequest(() => campaignApi.setElectronicCommunication(data), handleError, handleSuccess);
        },
        [dispatch, handleRequest],
    );

    return {
        form,
        formState,
        isProcessing,
        onSubmit,
        campaignData,
    };
}
